import { ChevronDownIcon } from "lucide-react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  RichTextDisplay,
  Row,
  Text,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useGetOtherEvaluations } from "@/hooks/queries/useGetOtherEvaluations";
import { dates } from "@/lib/dates";
import { GetOtherEvaluationsResponse } from "@/types";

export const PastEvaluations = ({
  candidacyId,
  talentId,
}: {
  candidacyId: string;
  talentId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: otherEvaluations,
    isError,
    isPending,
  } = useGetOtherEvaluations({ candidacyId, talentId });

  /* Render */
  const renderEvaluation = (evaluation: GetOtherEvaluationsResponse) => {
    const followUps = Array.isArray(evaluation.evaluation.followUps)
      ? evaluation.evaluation.followUps
      : [];

    return (
      <Column
        className="gap-5 border-b border-slate-100 py-5 last:border-b-0"
        key={evaluation.candidacyId}
      >
        <Column className="gap-1.5">
          {evaluation.evaluationLastUpdatedAt ? (
            <Text className="w-fit rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-slate-500">
              {dates.formatShortDate(evaluation.evaluationLastUpdatedAt)}
            </Text>
          ) : null}
          <Text className="text-sm font-semibold text-slate-800">
            {evaluation.jobTitle}
          </Text>
          <Text className="text-sm font-normal text-slate-500">
            {evaluation.companyName}
          </Text>
        </Column>
        <Column className="gap-2">
          <Text className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Added notes
          </Text>
          <RichTextDisplay
            className="text-sm font-normal leading-relaxed text-slate-700"
            richText={evaluation.evaluation.evaluation}
          />
        </Column>
        <Column className="gap-2">
          <Text className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Recommended followups
          </Text>
          {followUps.length ? (
            <Column className="gap-3">
              {followUps.map(({ note, subtitle, title }) => (
                <Column className="gap-1" key={`${title}-${subtitle}`}>
                  <Text className="text-sm font-medium text-slate-700">
                    {title}
                  </Text>
                  <Text className="text-sm font-normal text-slate-500">
                    {subtitle}
                  </Text>
                  {note ? (
                    <Text className="text-sm font-normal text-slate-600">
                      {note}
                    </Text>
                  ) : null}
                </Column>
              ))}
            </Column>
          ) : (
            <Text className="text-sm font-normal text-slate-400">
              No followups were recommended.
            </Text>
          )}
        </Column>
      </Column>
    );
  };

  const render = () => {
    if (isPending) return <LoadingIndicator variant="medium" />;
    if (isError)
      return <ErrorText errorMessage="Error retrieving past evaluations." />;
    if (otherEvaluations.length === 0) return null;

    return (
      <Collapsible className="group/past-evaluations">
        <CollapsibleTrigger asChild>
          <Button
            className="h-auto w-full justify-between rounded-none border-0 border-t border-slate-200 bg-transparent px-0 py-4 text-slate-700 hover:bg-transparent"
            type="button"
            variant="ghost"
          >
            <Row className="items-center gap-2">
              <Text className="text-sm font-medium text-slate-700">
                Past Evaluations
              </Text>
              <Text className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                {otherEvaluations.length}
              </Text>
            </Row>
            <ChevronDownIcon className="h-4 w-4 text-slate-400 transition-transform group-data-[state=open]/past-evaluations:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column>{otherEvaluations.map(renderEvaluation)}</Column>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return render();
};
